import api from "../api";
import type { ManualDto } from "../models/Manual";
import type { Manual } from "../models/ManualAuthoring";

type ApiResponse<T> = {
  statusCode: number;
  message?: string;
  data?: T;
  error?: string | null;
};

export type ManualCreateDto = {
  masterAssemblyId: number;
  manualType: string;
  title: string;
  status: string;
  reviewerComments?: string | null;
  version: number;
  createdOn: string;
  updatedOn?: string | null;
  createdBy: string;
  updatedBy?: string | null;
};

export type ManualTranslationLatestDto = {
  id: number;
  languageCode: string;
  contentId?: number;
  vendor?: string | null;
  translatedJson?: string;
  status?: string | null;
  createdOn?: string | null;
  updatedOn?: string | null;
  latestCompletedOn?: string | null;
  latestQueueId?: number | null;
  latestQueueStatus?: string | null;
  latestQueueCreatedOn?: string | null;
  latestQueueStartedOn?: string | null;
  latestQueueCompletedOn?: string | null;
  latestQueueErrorMessage?: string | null;
  hasNewerAttemptAfterCompleted?: boolean;
};

export type CreateManualTranslationQueuePayload = {
  contentId: number;
  elementId?: number | null;
  elementType?: string | null;
  languageCode: string;
  vendor?: string;
  priority?: number;
  status?: string;
};

export type ManualTranslationQueueItem = {
  queueId: number;
  contentId: number;
  elementId?: number | null;
  elementType?: string | null;
  languageCode: string;
  vendor?: string | null;
  priority?: number | null;
  status?: string | null;
  createdOn?: string | null;
  startedOn?: string | null;
  completedOn?: string | null;
  errorMessage?: string | null;
};

/* -------------------------------------------------------
   Manual header APIs
------------------------------------------------------- */

export async function fetchManualsByMasterAssembly(
  oem: string,
  masterAssemblyId: number
): Promise<ManualDto[]> {
  const { data } = await api.get<ApiResponse<ManualDto[]>>(
    `/Manuals/${oem}/by-master-assembly/${masterAssemblyId}`
  );

  if (data?.statusCode === 200 && Array.isArray(data.data)) return data.data;
  return [];
}

export async function createManual(
  oem: string,
  payload: ManualCreateDto,
  reuseManualId?: number
): Promise<ManualDto> {
  const reuseParam =
    reuseManualId != null && !Number.isNaN(reuseManualId)
      ? `?reuseManualId=${reuseManualId}`
      : "";

  const { data } = await api.post<ApiResponse<ManualDto>>(
    `/Manuals/${oem}${reuseParam}`,
    payload
  );

  if (data?.statusCode && data.statusCode >= 200 && data.statusCode < 300 && data.data) {
    return data.data;
  }

  throw new Error(data?.message || "Failed to create manual");
}

export async function updateManual(
  oem: string,
  payload: ManualCreateDto & { manualId: number },
  reuseManualId?: number
): Promise<ManualDto> {
  const reuseParam =
    reuseManualId != null && !Number.isNaN(reuseManualId)
      ? `?reuseManualId=${reuseManualId}`
      : "";

  const { data } = await api.put<ApiResponse<ManualDto>>(
    `/Manuals/${oem}${reuseParam}`,
    payload
  );

  if (data?.statusCode && data.statusCode >= 200 && data.data) {
    return data.data;
  }

  throw new Error(data?.message || "Failed to update manual");
}

/* -------------------------------------------------------
   CONTENT (Hierarchy JSON)
------------------------------------------------------- */

type ManualContentRow = {
  contentId: number;
  jsonContent: string | null;
  isActive: boolean;
  modifiedOn?: string;
};

/**
 * Load hierarchy by manualId
 */
export async function fetchManualJson(
  oem: string,
  manualId: number
): Promise<{ contentId: number; manual: Manual }> {
  const { data } = await api.get<ApiResponse<ManualContentRow[]>>(
    `/Manuals/${oem}/content/by-manual/${manualId}`
  );

  if (!data?.data?.length) {
    return {
      contentId: 0,
      manual: { sections: [] },
    };
  }

  const row = data.data[0];

  return {
    contentId: row.contentId,
    manual: row.jsonContent ? JSON.parse(row.jsonContent) : { sections: [] },
  };
}

/**
 * Save hierarchy JSON
 */
export async function updateManualContent(
  oem: string,
  payload: {
    contentId: number;
    jsonContent: string;
    isActive: boolean;
    modifiedOn: string;
  }
) {
  const { data } = await api.put<ApiResponse<any>>(
    `/Manuals/${oem}/content`,
    payload
  );

  if (!data || data.statusCode !== 200) {
    throw new Error(data?.message || "Manual content update failed");
  }

  return data.data;
}

export async function fetchLatestManualTranslationsByManual(
  oem: string,
  manualId: number
): Promise<ManualTranslationLatestDto[]> {
  if (!oem?.trim()) {
    throw new Error("OEM is required.");
  }
  if (!manualId || Number.isNaN(manualId)) {
    throw new Error("Valid manual id is required.");
  }

  const { data } = await api.get<ApiResponse<ManualTranslationLatestDto[]>>(
    `/Manuals/${oem}/translations/latest/by-manual/${manualId}`
  );

  if (data?.statusCode === 200 && Array.isArray(data.data)) {
    return data.data;
  }

  if (data?.statusCode === 204) {
    return [];
  }

  throw new Error(
    data?.message || data?.error || "Failed to load translated manuals."
  );
}

export async function fetchManualTranslationJson(
  oem: string,
  translationId: number
): Promise<Manual> {
  if (!oem?.trim()) {
    throw new Error("OEM is required.");
  }
  if (!translationId || Number.isNaN(translationId)) {
    throw new Error("Valid translation id is required.");
  }

  const { data } = await api.get<ApiResponse<Manual | null>>(
    `/Manuals/${oem}/translations/${translationId}`
  );

  if (data?.statusCode === 200 && data.data) {
    return data.data;
  }

  throw new Error(
    data?.message || data?.error || "Failed to load manual translation."
  );
}

export async function createManualTranslationQueue(
  oem: string,
  payload: CreateManualTranslationQueuePayload
): Promise<ManualTranslationQueueItem> {
  if (!oem?.trim()) {
    throw new Error("OEM is required.");
  }
  if (!payload?.contentId || Number.isNaN(payload.contentId)) {
    throw new Error("Valid content id is required.");
  }
  if (!payload?.languageCode?.trim()) {
    throw new Error("Language code is required.");
  }

  const body: CreateManualTranslationQueuePayload = {
    contentId: payload.contentId,
    elementId: payload.elementId ?? null,
    elementType: payload.elementType ?? null,
    languageCode: payload.languageCode.trim().toLowerCase(),
    vendor: payload.vendor ?? "lar1",
    priority: payload.priority ?? 0,
    status: payload.status ?? "pending",
  };

  const { data } = await api.post<ApiResponse<ManualTranslationQueueItem>>(
    `/Manuals/${oem}/translation-queue`,
    body
  );

  const isSuccess = Boolean(
    data?.statusCode && data.statusCode >= 200 && data.statusCode < 300
  );

  if (isSuccess && data.data) {
    return data.data;
  }

  throw new Error(
    data?.message || data?.error || "Failed to create manual translation queue."
  );
}
